import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { HelivanLoading } from '../components/HelivanLoading';

export const HelivanStatsigProvider = ({ children }: PropsWithChildren) => {
  const { backendUser } = useAuth();
  // We know backendUser exists because this component is only rendered when it does
  const user = {
    userID: backendUser!.id,
    customIDs: {
      userId: backendUser!.id,
      organizationId: backendUser!.currentOrganization?.id,
    },
  };

  const { client } = useClientAsyncInit(
    'client-jCAPqX9FxoMOSFH9t1rPCkGEOoqOiTMyhiEk4eYwO2',
    user,
    {
      plugins: [
        new StatsigAutoCapturePlugin(),
        new StatsigSessionReplayPlugin(),
      ],
    }
  );

  // Keep Statsig's user in sync when the organization changes WITHOUT remounting the
  // provider (the provider is keyed on user id only, so org changes don't remount).
  // Skips the first run since useClientAsyncInit already initialized with this user.
  const orgId = backendUser?.currentOrganization?.id;
  const didInit = useRef(false);
  useEffect(() => {
    if (!client) return;
    if (!didInit.current) {
      didInit.current = true;
      return;
    }
    client.updateUserAsync(user).catch((error) => {
      console.error('Failed to update Statsig user on org change:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // When the component unmounts (user changes), shutdown the client
      if (client) {
        client.shutdown().catch((error) => {
          console.error('Failed to shutdown Statsig client:', error);
        });
      }
    };
  }, [client]);

  if (!client) {
    return <HelivanLoading />;
  }

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
};
